/**
 * Gallery Lazy Loader — Lazy DOM / Virtualized Gallery for Shopify Product Pages
 *
 * Reduces initial DOM size for products with many images by deferring
 * the rendering of media items beyond a configurable limit. Deferred items
 * are stored as JSON and hydrated into the DOM on user interaction or
 * variant change.
 *
 * Key behaviors:
 * - IntersectionObserver triggers hydration when gallery nears viewport
 * - User scroll/touch on carousel triggers immediate hydration
 * - Variant change (capturing phase) triggers immediate hydration before
 *   ProductGallery handler runs — critical for color filtering to work
 * - Thumbnails and page dots are hydrated alongside main media
 */
(function () {
  'use strict';

  // Standard Shopify image widths for srcset generation
  var IMAGE_WIDTHS = [200, 300, 400, 500, 600, 700, 800, 1000, 1200, 1400, 1600, 1800, 2000, 2200, 2400, 2600, 2800, 3000, 3200];

  /**
   * Replace the width parameter in a Shopify CDN URL.
   * Handles both ?width=N and &width=N patterns.
   */
  function replaceWidth(url, width) {
    return url.replace(/([?&])width=\d+/, '$1width=' + width);
  }

  /**
   * Build a srcset string from a base Shopify CDN URL.
   */
  function buildSrcset(baseUrl, maxWidth, widths) {
    var applicable = widths.filter(function (w) { return w <= maxWidth; });
    if (applicable.indexOf(maxWidth) === -1) applicable.push(maxWidth);
    return applicable
      .map(function (w) { return replaceWidth(baseUrl, w) + ' ' + w + 'w'; })
      .join(', ');
  }

  // ────────────────────────────────────────────────────────────────────────────
  // GalleryLazyLoader
  // ────────────────────────────────────────────────────────────────────────────

  function GalleryLazyLoader(galleryEl) {
    this.gallery = galleryEl;
    this.dataEl = galleryEl.querySelector('script[data-deferred-gallery]');

    if (!this.dataEl) return;

    try {
      this.data = JSON.parse(this.dataEl.textContent);
    } catch (e) {
      console.warn('[GalleryLazyLoader] Failed to parse deferred gallery data:', e);
      return;
    }

    if (!this.data.media || this.data.media.length === 0) return;

    this._carousel = galleryEl.querySelector('media-carousel');
    this._formId = galleryEl.getAttribute('form');
    this._form = this._formId ? document.forms[this._formId] : null;
    this._hydrated = false;
    this._zoomIconHtml = null;
    this._observer = null;

    this._init();
  }

  GalleryLazyLoader.prototype._init = function () {
    var self = this;

    // ── Variant change: capture phase (runs BEFORE ProductGallery handler) ──
    if (this._form) {
      this._form.addEventListener(
        'variant:change',
        function () { self._onVariantChange(); },
        true // capturing phase
      );
    }

    // ── IntersectionObserver — hydrate when gallery approaches viewport ──
    if ('IntersectionObserver' in window) {
      this._observer = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) {
              self._scheduleHydration();
              self._observer.disconnect();
              self._observer = null;
              break;
            }
          }
        },
        { rootMargin: '300px' }
      );
      this._observer.observe(this.gallery);
    } else {
      setTimeout(function () { self._scheduleHydration(); }, 500);
    }

    // ── First user interaction with carousel ──
    if (this._carousel) {
      var onInteraction = function () {
        self._hydrateAll();
        self._carousel.removeEventListener('scroll', onInteraction);
        self._carousel.removeEventListener('touchstart', onInteraction);
      };
      this._carousel.addEventListener('scroll', onInteraction, { passive: true });
      this._carousel.addEventListener('touchstart', onInteraction, { passive: true });
    }
  };

  /**
   * Schedule non-blocking hydration via requestIdleCallback.
   */
  GalleryLazyLoader.prototype._scheduleHydration = function () {
    if (this._hydrated) return;
    var self = this;

    if ('requestIdleCallback' in window) {
      requestIdleCallback(function () { self._hydrateAll(); }, { timeout: 3000 });
    } else {
      setTimeout(function () { self._hydrateAll(); }, 200);
    }
  };

  /**
   * Variant change handler. Runs in capturing phase so the DOM is ready
   * before ProductGallery._onVariantChanged fires.
   */
  GalleryLazyLoader.prototype._onVariantChange = function () {
    if (this._hydrated) return;
    this._hydrateAll();
  };

  // ────────────────────────────────────────────────────────────────────────────
  // Full hydration
  // ────────────────────────────────────────────────────────────────────────────

  GalleryLazyLoader.prototype._hydrateAll = function () {
    if (this._hydrated) return;
    this._hydrated = true;

    // Stop observing
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }

    // Cache zoom icon from an existing button (if any)
    var existingZoomBtn = this.gallery.querySelector('.product-gallery__zoom .circle-button');
    if (existingZoomBtn) {
      this._zoomIconHtml = existingZoomBtn.innerHTML;
    }

    this._hydrateMainMedia();
    this._hydrateThumbnails();
    this._hydratePageDots();

    // Clean up JSON data node
    if (this.dataEl && this.dataEl.parentNode) {
      this.dataEl.parentNode.removeChild(this.dataEl);
    }
    this.dataEl = null;
  };

  // ────────────────────────────────────────────────────────────────────────────
  // Main media hydration
  // ────────────────────────────────────────────────────────────────────────────

  GalleryLazyLoader.prototype._hydrateMainMedia = function () {
    if (!this._carousel) return;

    var fragment = document.createDocumentFragment();
    var media = this.data.media;

    for (var i = 0; i < media.length; i++) {
      fragment.appendChild(this._createMediaElement(media[i]));
    }

    // Single DOM insertion — one reflow
    this._carousel.appendChild(fragment);

    // Invalidate BaseCarousel._items cache
    this._carousel._items = null;
  };

  /**
   * Create a main media element matching the server-rendered structure.
   */
  GalleryLazyLoader.prototype._createMediaElement = function (data) {
    var div = document.createElement('div');
    var cls = 'product-gallery__media';
    if (data.expand) cls += ' product-gallery__media--expand';
    if (this.data.mobileCarouselControl !== 'free_scroll') cls += ' snap-center';
    div.className = cls;

    div.setAttribute('data-media-type', data.mediaType);
    div.setAttribute('data-media-id', String(data.id));

    if (data.filtered) {
      div.hidden = true;
    }

    if (data.mediaType === 'image') {
      div.appendChild(this._createImage(data));

      // Zoom button for grid layouts
      if (this.data.enableImageZoom && this.data.desktopMediaLayout.indexOf('grid') !== -1) {
        div.appendChild(this._createZoomButton());
      }
    }

    return div;
  };

  /**
   * Create an <img> with proper srcset, sizes, loading="lazy", decoding="async".
   */
  GalleryLazyLoader.prototype._createImage = function (data) {
    var img = document.createElement('img');
    img.loading = 'lazy';
    img.decoding = 'async';
    img.className = 'rounded';
    img.alt = data.alt || '';
    img.sizes = data.sizes || '';

    var w = data.previewImageWidth || 1000;
    var h = data.previewImageHeight || Math.round(w / (data.aspectRatio || 1));
    img.width = w;
    img.height = h;

    if (data.src) {
      img.srcset = buildSrcset(data.src, w, IMAGE_WIDTHS);
      img.src = data.src;
    }

    return img;
  };

  /**
   * Create a zoom button matching the existing server-rendered structure.
   */
  GalleryLazyLoader.prototype._createZoomButton = function () {
    var wrapper = document.createElement('div');
    wrapper.className = 'product-gallery__zoom hidden md:block';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('is', 'product-zoom-button');
    btn.className = 'circle-button circle-button--fill ring';

    var srSpan = document.createElement('span');
    srSpan.className = 'sr-only';
    srSpan.textContent = (window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.zoomGallery) || 'Zoom';
    btn.appendChild(srSpan);

    // Reuse icon SVG from an existing zoom button
    if (this._zoomIconHtml) {
      var temp = document.createElement('div');
      temp.innerHTML = this._zoomIconHtml;
      var children = temp.children;
      for (var i = 0; i < children.length; i++) {
        if (!children[i].classList || !children[i].classList.contains('sr-only')) {
          btn.appendChild(children[i].cloneNode(true));
        }
      }
    }

    wrapper.appendChild(btn);
    return wrapper;
  };

  // ────────────────────────────────────────────────────────────────────────────
  // Thumbnail hydration
  // ────────────────────────────────────────────────────────────────────────────

  GalleryLazyLoader.prototype._hydrateThumbnails = function () {
    var thumbnailList = this.gallery.querySelector('.product-gallery__thumbnail-list');
    if (!thumbnailList || !thumbnailList.items) return;

    var existingItemCount = thumbnailList.items.length;
    var fragment = document.createDocumentFragment();
    var addedCount = 0;
    var media = this.data.media;

    for (var i = 0; i < media.length; i++) {
      if (!media[i].needsThumbnail) continue; // already rendered by Liquid
      fragment.appendChild(this._createThumbnail(media[i]));
      addedCount++;
    }

    if (addedCount === 0) return;

    thumbnailList.appendChild(fragment);

    // Update PageDots items cache
    thumbnailList.items = Array.from(thumbnailList.children);

    // Bind click handlers for new items only
    for (var j = existingItemCount; j < thumbnailList.items.length; j++) {
      (function (idx, container) {
        container.items[idx].addEventListener('click', function () {
          container.select(idx);
        });
      })(j, thumbnailList);
    }
  };

  /**
   * Create a thumbnail button element.
   */
  GalleryLazyLoader.prototype._createThumbnail = function (data) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'product-gallery__thumbnail';
    if (data.filtered) btn.hidden = true;
    btn.setAttribute('aria-current', 'false');
    btn.setAttribute('aria-label', 'Go to item ' + data.position);

    var img = document.createElement('img');
    img.loading = 'lazy';
    img.decoding = 'async';
    img.className = 'object-contain rounded-sm';
    img.alt = data.alt || '';
    img.sizes = '(max-width: 699px) 56px, 64px';

    if (data.thumbnailSrc) {
      var thumbWidths = [56, 64, 112, 128, 168, 192];
      img.srcset = buildSrcset(data.thumbnailSrc, 192, thumbWidths);
      img.src = data.thumbnailSrc;
    }

    btn.appendChild(img);
    return btn;
  };

  // ────────────────────────────────────────────────────────────────────────────
  // Page dots hydration (mobile navigation)
  // ────────────────────────────────────────────────────────────────────────────

  GalleryLazyLoader.prototype._hydratePageDots = function () {
    var containers = this.gallery.querySelectorAll(
      'page-dots:not(.product-gallery__thumbnail-list)'
    );

    for (var c = 0; c < containers.length; c++) {
      var container = containers[c];
      if (!container.items) continue;

      var existingItemCount = container.items.length;
      var fragment = document.createDocumentFragment();
      var media = this.data.media;

      for (var i = 0; i < media.length; i++) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tap-area';
        if (media[i].filtered) btn.hidden = true;
        btn.setAttribute('aria-current', 'false');
        btn.innerHTML = '<span class="sr-only">Go to item ' + media[i].position + '</span>';
        fragment.appendChild(btn);
      }

      container.appendChild(fragment);
      container.items = Array.from(container.children);

      // Bind click handlers for new dots
      for (var j = existingItemCount; j < container.items.length; j++) {
        (function (idx, cont) {
          cont.items[idx].addEventListener('click', function () {
            cont.select(idx);
          });
        })(j, container);
      }
    }
  };

  // ────────────────────────────────────────────────────────────────────────────
  // Initialization
  // ────────────────────────────────────────────────────────────────────────────

  function init() {
    var galleries = document.querySelectorAll('product-gallery');
    for (var i = 0; i < galleries.length; i++) {
      new GalleryLazyLoader(galleries[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
